import React, { useState, useRef } from 'react';
import classNames from 'classnames';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { InputWithUnit } from '../../../../components/inputs/Input/InputWithUnit';
import { BaseInputProps } from './BaseInput';
import useT from '../../../../translation/src/hooks/useT';
import { GetUnit } from '../../utils/getUnit';
import { ConvertType, useCondMeterToFeet, useCondFeetToMeter } from '../../../../hooks/useFormatNumber';

export const FloatInput: React.FC<BaseInputProps<number>> = ({ instance, onChange, disabled = false, className }) => {
  const { config, value } = instance;
  const mixedText = useT('mixed', { swc: true });
  const mixedStateTooltip = useT('Different values selected', { swc: true });
  const convertType = config.ui?.useUserPreferenceConvertType || ConvertType.NONE;
  const { condMeterToFeet } = useCondMeterToFeet();
  const { condFeetToMeter } = useCondFeetToMeter();

  const [isFocused, setIsFocused] = useState(false);
  // The user's draft. Only shown while focused; seeded from the external value on focus, so
  // an external update never clobbers what is being typed and no draft sync is needed while
  // unfocused (the input then shows the external value directly).
  const [localValue, setLocalValue] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);

  /** Meters → the user's unit, with the configured number of decimals. */
  const format = (meters: number): string => {
    const converted = condMeterToFeet(meters, convertType);
    const decimals = config.ui?.decimals;
    return decimals !== undefined ? converted.toFixed(decimals) : converted.toString();
  };
  const formattedExternalValue = typeof value === 'number' ? format(value) : '';

  const applyConstraints = (numValue: number): number => {
    let constrainedValue = numValue;
    if (config.validation?.min !== undefined && constrainedValue < config.validation.min) {
      constrainedValue = config.validation.min;
    }
    if (config.validation?.max !== undefined && constrainedValue > config.validation.max) {
      constrainedValue = config.validation.max;
    }
    return constrainedValue;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setLocalValue(inputValue);

    const numValue = parseFloat(inputValue);
    if (!isNaN(numValue)) {
      // Convert user input to meters (condFeetToMeter handles the conversion based on user preference)
      const numValueInMeters = condFeetToMeter(numValue, convertType);
      const constrainedValue = applyConstraints(numValueInMeters);
      onChange(constrainedValue);
    }
  };

  const handleFocus = () => {
    setLocalValue(formattedExternalValue);
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
    commitValue();
  };

  const commitValue = () => {
    const numValue = parseFloat(localValue);
    if (!isNaN(numValue)) {
      // Convert user input to meters
      const numValueInMeters = condFeetToMeter(numValue, convertType);
      const constrainedValue = applyConstraints(numValueInMeters);
      onChange(constrainedValue);

      // Update local value to show the constrained result
      setLocalValue(format(constrainedValue));
    } else {
      // Invalid input - revert to current value
      setLocalValue(format(typeof value === 'number' ? value : 0));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitValue();
      inputRef.current?.blur();
    }
  };

  const displayValue = isFocused ? localValue : formattedExternalValue;
  return (
    <div className={classNames('relative flex-1', className)}>
      <InputWithUnit
        ref={inputRef}
        type="number"
        value={instance.isMixed && !isFocused ? '' : displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={instance.isMixed ? mixedText : ''}
        disabled={disabled || config.disabled}
        className={classNames('w-full', instance.isMixed && !isFocused && 'text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-800 italic pr-8')}
        step={config.validation?.step || 0.01}
        min={config.validation?.min}
        max={config.validation?.max}
        unitString={GetUnit(config.ui)}
      />
      {instance.isMixed && !isFocused && <QuestionMarkCircleIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neon-green-500 pointer-events-none z-20" title={mixedStateTooltip} />}
    </div>
  );
};
